import Agilite from 'agilite'
import BookingEnums from '../../../Bookings/utils/enums'
import { createLookup } from '../../../lib/utils'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const countBookings = (filter) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(BookingEnums.profileKeys.BOOKINGS, BookingEnums.routeKeys.COUNT, {
          filter: JSON.stringify(filter)
        })
        resolve(response.data.result)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const countBookingsAggregate = (filter) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Build the same pipeline as readBookingsAggregate, but without pagination
        const pipeline = []

        // Handle filter with date conversion
        if (filter) {
          const { bookingDate, ...restFilter } = { ...filter }

          // Add non-date filters
          if (Object.keys(restFilter).length > 0) {
            pipeline.push({ $match: restFilter })
          }

          // Handle date filters
          if (bookingDate) {
            const dateConditions = Object.entries(bookingDate)
              .filter(([op]) => ['$gt', '$lt', '$gte', '$lte', '$eq'].includes(op))
              .map(([op, value]) => ({
                [op]: ['$bookingDate', { $dateFromString: { dateString: value } }]
              }))

            if (dateConditions.length > 0) {
              pipeline.push({
                $match: { $expr: { $and: dateConditions } }
              })
            }
          }
        }

        // Only add a count stage instead of all the lookups and projections
        pipeline.push({ $count: 'total' })

        // Execute aggregation
        const countResponse = await agilite.Connectors.execute(
          BookingEnums.profileKeys.BOOKINGS,
          BookingEnums.routeKeys.AGGREGATE,
          { pipeline: JSON.stringify(pipeline) }
        )

        const total = countResponse.data[0]?.total || 0
        resolve(total)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readBookingsAggregate = (filter, page, pageLimit, options) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Build pipeline
        const pipeline = []

        // Handle filter with date conversion
        if (filter) {
          const { bookingDate, ...restFilter } = { ...filter }

          // Add non-date filters
          if (Object.keys(restFilter).length > 0) {
            pipeline.push({ $match: restFilter })
          }

          // Handle date filters
          if (bookingDate) {
            const dateConditions = Object.entries(bookingDate)
              .filter(([op]) => ['$gt', '$lt', '$gte', '$lte', '$eq'].includes(op))
              .map(([op, value]) => ({
                [op]: ['$bookingDate', { $dateFromString: { dateString: value } }]
              }))

            if (dateConditions.length > 0) {
              pipeline.push({
                $match: { $expr: { $and: dateConditions } }
              })
            }
          }
        }
        if (options?.sort) {
          pipeline.push({
            $sort: {
              bookingDate: -1,
              startTime: 1
            }
          })
        }
        // Add ID conversion and lookups
        pipeline.push(
          // Convert string IDs to ObjectIds
          {
            $addFields: {
              userRefObjectId: {
                $cond: [
                  { $and: [{ $ne: ['$userRef', null] }, { $ne: ['$userRef', ''] }, { $ne: ['$userRef', undefined] }] },
                  { $toObjectId: '$userRef' },
                  null
                ]
              },
              clinicRefObjectId: {
                $cond: [
                  {
                    $and: [
                      { $ne: ['$clinicRef', null] },
                      { $ne: ['$clinicRef', ''] },
                      { $ne: ['$clinicRef', undefined] },
                      { $ne: ['$clinicRef', 'Home Visit'] }
                    ]
                  },
                  { $toObjectId: '$clinicRef' },
                  null
                ]
              },
              medicalProfRefObjectId: {
                $cond: [
                  {
                    $and: [
                      { $ne: ['$medicalProfRef', null] },
                      { $ne: ['$medicalProfRef', ''] },
                      { $ne: ['$medicalProfRef', undefined] }
                    ]
                  },
                  { $toObjectId: '$medicalProfRef' },
                  null
                ]
              }
            }
          },

          // Patient lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            'userRefObjectId',
            '_id',
            'patientRecord',
            {
              firstName: 1,
              lastName: 1,
              idNo: 1,
              dateOfBirth: 1,
              gender: 1,
              race: 1,
              extraData: 1,
              residentialAddress: 1,
              email: 1,
              phoneNumber: 1,
              medicalAid: 1,
              mainMemberId: 1,
              billingId: 1
            }
          ),

          // Clinic lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_admin_clinics`,
            'clinicRefObjectId',
            '_id',
            'clinicRecord',
            { clinicLogo: 0 }
          ),

          // Medical professional lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            'medicalProfRefObjectId',
            '_id',
            'medicalProfRecord',
            {
              firstName: 1,
              lastName: 1,
              'extraData.profession': 1
            }
          ),

          // Unwind arrays
          { $unwind: { path: '$patientRecord', preserveNullAndEmptyArrays: true } },
          { $unwind: { path: '$clinicRecord', preserveNullAndEmptyArrays: true } },
          { $unwind: { path: '$medicalProfRecord', preserveNullAndEmptyArrays: true } },

          // Project final fields
          {
            $project: {
              _id: 1,
              userRef: 1,
              clinicRef: 1,
              medicalProfRef: 1,
              isVirtualVisit: 1,
              callType: 1,
              startTime: 1,
              endTime: 1,
              bookingDate: 1,
              chiefComplaint: 1,
              status: 1,
              service: 1,
              dateCreated: 1,
              symptoms: 1,
              diagnosis: 1,
              processRef: 1,
              otp: 1,
              wasNoShow: 1,
              paymentMethod: 1,
              serviceRecord: 1,
              noCharge: 1,
              consultationRequired: 1,
              nurseProcedureRequired: 1,
              callpayGatewayReference: 1,

              // Computed fields
              patientName: {
                $cond: [
                  { $ifNull: ['$patientRecord', false] },
                  {
                    $concat: [
                      { $ifNull: ['$patientRecord.firstName', ''] },
                      ' ',
                      { $ifNull: ['$patientRecord.lastName', ''] }
                    ]
                  },
                  ''
                ]
              },
              patientAddress: { $ifNull: ['$patientRecord.residentialAddress', ''] },
              clinicName: {
                $cond: [{ $eq: ['$clinicRef', 'Home Visit'] }, 'Home Visit', { $ifNull: ['$clinicRecord.name', ''] }]
              },
              professionalName: {
                $cond: [
                  { $ifNull: ['$medicalProfRecord', false] },
                  {
                    $concat: [
                      { $ifNull: ['$medicalProfRecord.firstName', ''] },
                      ' ',
                      { $ifNull: ['$medicalProfRecord.lastName', ''] }
                    ]
                  },
                  ''
                ]
              },
              profession: {
                $cond: [
                  {
                    $and: [
                      { $ifNull: ['$medicalProfRecord', false] },
                      { $ifNull: ['$medicalProfRecord.extraData.profession', false] }
                    ]
                  },
                  {
                    $concat: [
                      { $toUpper: { $substr: ['$medicalProfRecord.extraData.profession', 0, 1] } },
                      {
                        $substr: [
                          '$medicalProfRecord.extraData.profession',
                          1,
                          { $subtract: [{ $strLenCP: '$medicalProfRecord.extraData.profession' }, 1] }
                        ]
                      }
                    ]
                  },
                  ''
                ]
              },

              // Keep record objects
              patientRecord: 1,
              clinicRecord: 1,
              medicalProfRecord: 1
            }
          }
        )

        // Add pagination
        if (page && pageLimit) {
          pipeline.push({ $skip: (page - 1) * pageLimit }, { $limit: parseInt(pageLimit) })
        }

        // Execute aggregation
        const bookingResponse = await agilite.Connectors.execute(
          BookingEnums.profileKeys.BOOKINGS,
          BookingEnums.routeKeys.AGGREGATE,
          { pipeline: JSON.stringify(pipeline) }
        )

        resolve(bookingResponse.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
